import { api, handleApiResponse, handleApiError } from '@/lib/api';
import type {
  MarketplaceCategory,
  MarketplaceProduct,
  MarketplaceProductsResponse,
  MarketplaceOffer,
} from '@/types';

const BASE = '/marketplace';

export const marketplaceService = {
  // Categories
  async listCategories(params?: {
    companyId?: string | null;
    isActive?: boolean;
    parentId?: string | null;
  }): Promise<MarketplaceCategory[]> {
    try {
      const search = new URLSearchParams();
      if (params?.companyId != null) search.set('companyId', params.companyId);
      if (params?.isActive !== undefined) search.set('isActive', String(params.isActive));
      if (params?.parentId != null) search.set('parentId', params.parentId);
      const q = search.toString();
      const response = await api.get<{ success: boolean; data: MarketplaceCategory[] }>(
        `${BASE}/categories${q ? `?${q}` : ''}`
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getCategory(id: string): Promise<MarketplaceCategory> {
    try {
      const response = await api.get<{ success: boolean; data: MarketplaceCategory }>(
        `${BASE}/categories/${id}`
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async createCategory(data: {
    name: string;
    slug?: string;
    description?: string | null;
    imageUrl?: string | null;
    isActive?: boolean;
    sortOrder?: number;
    companyId?: string | null;
    parentId?: string | null;
  }): Promise<MarketplaceCategory> {
    try {
      const response = await api.post<{ success: boolean; data: MarketplaceCategory }>(
        `${BASE}/categories`,
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateCategory(
    id: string,
    data: Partial<{
      name: string;
      slug: string;
      description: string | null;
      imageUrl: string | null;
      isActive: boolean;
      sortOrder: number;
      companyId: string | null;
      parentId: string | null;
    }>
  ): Promise<MarketplaceCategory> {
    try {
      const response = await api.put<{ success: boolean; data: MarketplaceCategory }>(
        `${BASE}/categories/${id}`,
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async deleteCategory(id: string): Promise<void> {
    try {
      await api.delete(`${BASE}/categories/${id}`);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  // Products
  async listProducts(params?: {
    companyId?: string;
    categoryId?: string;
    productType?: 'PRODUCT' | 'SERVICE';
    isActive?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<MarketplaceProductsResponse> {
    try {
      const search = new URLSearchParams();
      if (params?.companyId) search.set('companyId', params.companyId);
      if (params?.categoryId) search.set('categoryId', params.categoryId);
      if (params?.productType) search.set('productType', params.productType);
      if (params?.isActive !== undefined) search.set('isActive', String(params.isActive));
      if (params?.search) search.set('search', params.search);
      if (params?.page) search.set('page', String(params.page));
      if (params?.limit) search.set('limit', String(params.limit));
      const q = search.toString();
      const response = await api.get<{ success: boolean; data: MarketplaceProductsResponse }>(
        `${BASE}/products${q ? `?${q}` : ''}`
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getProduct(id: string): Promise<MarketplaceProduct> {
    try {
      const response = await api.get<{ success: boolean; data: MarketplaceProduct }>(
        `${BASE}/products/${id}`
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async createProduct(data: {
    companyId: string;
    categoryId: string;
    name: string;
    slug?: string;
    description?: string | null;
    productType?: 'PRODUCT' | 'SERVICE';
    price: number;
    compareAtPrice?: number | null;
    isActive?: boolean;
    stock?: number | null;
    sku?: string | null;
    metadata?: Record<string, unknown>;
  }): Promise<MarketplaceProduct> {
    try {
      const response = await api.post<{ success: boolean; data: MarketplaceProduct }>(
        `${BASE}/products`,
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateProduct(
    id: string,
    data: Partial<{
      categoryId: string;
      name: string;
      slug: string;
      description: string | null;
      productType: 'PRODUCT' | 'SERVICE';
      price: number;
      compareAtPrice: number | null;
      isActive: boolean;
      stock: number | null;
      sku: string | null;
      metadata: Record<string, unknown>;
    }>
  ): Promise<MarketplaceProduct> {
    try {
      const response = await api.put<{ success: boolean; data: MarketplaceProduct }>(
        `${BASE}/products/${id}`,
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async deleteProduct(id: string): Promise<void> {
    try {
      await api.delete(`${BASE}/products/${id}`);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async addProductImage(
    productId: string,
    data: { url: string; alt?: string | null; sortOrder?: number }
  ): Promise<{ id: string; productId: string; url: string; alt?: string | null; sortOrder: number }> {
    try {
      const response = await api.post<{
        success: boolean;
        data: { id: string; productId: string; url: string; alt?: string | null; sortOrder: number };
      }>(`${BASE}/products/${productId}/images`, data);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async removeProductImage(productId: string, imageId: string): Promise<void> {
    try {
      await api.delete(`${BASE}/products/${productId}/images/${imageId}`);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  // Offers
  async listOffers(params?: {
    productId?: string;
    isActive?: boolean;
    companyId?: string;
  }): Promise<MarketplaceOffer[]> {
    try {
      const search = new URLSearchParams();
      if (params?.productId) search.set('productId', params.productId);
      if (params?.isActive !== undefined) search.set('isActive', String(params.isActive));
      if (params?.companyId) search.set('companyId', params.companyId);
      const q = search.toString();
      const response = await api.get<{ success: boolean; data: MarketplaceOffer[] }>(
        `${BASE}/offers${q ? `?${q}` : ''}`
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getOffer(id: string): Promise<MarketplaceOffer> {
    try {
      const response = await api.get<{ success: boolean; data: MarketplaceOffer }>(
        `${BASE}/offers/${id}`
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async createOffer(data: {
    productId: string;
    title: string;
    discountPercent?: number | null;
    startsAt?: string | null;
    endsAt?: string | null;
    isActive?: boolean;
  }): Promise<MarketplaceOffer> {
    try {
      const response = await api.post<{ success: boolean; data: MarketplaceOffer }>(
        `${BASE}/offers`,
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateOffer(
    id: string,
    data: Partial<{
      title: string;
      discountPercent: number | null;
      startsAt: string | null;
      endsAt: string | null;
      isActive: boolean;
    }>
  ): Promise<MarketplaceOffer> {
    try {
      const response = await api.put<{ success: boolean; data: MarketplaceOffer }>(
        `${BASE}/offers/${id}`,
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async deleteOffer(id: string): Promise<void> {
    try {
      await api.delete(`${BASE}/offers/${id}`);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },
};
